import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import leftArrow from "../../assets/icons/left-arrow.svg";
import logoCobee from "../../assets/logo-cobee.svg";
import { Logout } from "../../components/Logout";
import Button from "../../components/button";
import { Footer } from "../../components/footer";
import { useMetrics } from "../../metrics";
import { RequestCardContext } from "../../requestCardProvider";
import { ROUTES } from "../../routes";
import classes from "./AddPhone.module.css";

interface CountryProps {
  flag: string;
  code: string;
  regexPhoneNumber: string;
}
const COMMON_PHONE_NUMBER_REGEX = "(?!(0))[0-9]{9}";

const COUNTRY_CODES: CountryProps[] = [
  {
    flag: "🇪🇸",
    code: "+34",
    regexPhoneNumber: COMMON_PHONE_NUMBER_REGEX,
  },
  {
    flag: "🇵🇹",
    code: "+351",
    regexPhoneNumber: COMMON_PHONE_NUMBER_REGEX,
  },
  {
    flag: "🇫🇷",
    code: "+33",
    regexPhoneNumber: COMMON_PHONE_NUMBER_REGEX,
  },
  {
    flag: "🇮🇹",
    code: "+39",
    regexPhoneNumber: "[0-9]{6,11}",
  },
  {
    flag: "🇲🇽",
    code: "+52",
    regexPhoneNumber: "(?!(0))[0-9]{10,12}",
  },
  {
    flag: "🇬🇧",
    code: "+44",
    regexPhoneNumber: "(?!(0))[0-9]{7,10}",
  },
  {
    flag: "🇩🇪",
    code: "+49",
    regexPhoneNumber: "(?!(0))[0-9]{6,13}",
  },
  {
    flag: "🇳🇱",
    code: "+31",
    regexPhoneNumber: COMMON_PHONE_NUMBER_REGEX,
  },
  {
    flag: "🇧🇪",
    code: "+32",
    regexPhoneNumber: "[0-9]{8,9}",
  },
  {
    flag: "🇮🇪",
    code: "+353",
    regexPhoneNumber: "[0-9]{7,11}",
  },
];

const AddPhone = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { t } = useTranslation();
  const { isFilledAddressFormData, setPhoneFormData } =
    useContext(RequestCardContext);
  const { pageView, trackAction } = useMetrics();
  const [regexPhoneNumber, setRegexPhoneNumber] = useState(
    COMMON_PHONE_NUMBER_REGEX
  );

  const handleForm = async (event: any) => {
    event.preventDefault();
    const { phoneNumber, countryCode } = event.target.elements;

    setPhoneFormData({
      phoneNumber: phoneNumber.value,
      countryCode: countryCode.value,
    });
    navigate(ROUTES.BIRTHDATE, { state });
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryCode = e.target.value;

    const result = COUNTRY_CODES.find(
      (country) => country.code === countryCode
    );
    if (result) {
      setRegexPhoneNumber(result.regexPhoneNumber);
    }
  };

  const handleOnClickFinish = () => {
    trackAction.click({
      screenName: "AddPhone",
      componentName: "button navigate to birthdate",
    });
  };

  const handleOnClickGoBack = () => {
    trackAction.click({
      screenName: "AddPhone",
      componentName: "button go back",
    });
  };

  const handlerOnInvalid = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.setCustomValidity(t("request-card.phone-number.number.error"));
  };

  const handlerOnInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.setCustomValidity("");
  };

  const getGoBackPath = () => {
    if (state?.virtualCard) return ROUTES.REQUEST_CARD;

    return ROUTES.ADD_ADDRESS;
  };

  useEffect(() => {
    if (isFilledAddressFormData() || state?.virtualCard) return;
    navigate(ROUTES.ADD_ADDRESS);
  }, [isFilledAddressFormData, navigate]);

  useEffect(() => {
    pageView("AddPhone");
  }, [pageView]);

  return (
    <div className={classes.container}>
      <header className="grid grid-cols-12 mt-4 h-16 items-center w-4/5">
        <Link
          className={`col-span-2 justify-end ${classes.goBack}`}
          to={getGoBackPath()}
          onClick={handleOnClickGoBack}
        >
          <img src={leftArrow} alt="" />
          {t("common.back")}
        </Link>
        <div className="flex justify-center col-start-3 col-span-8">
          <img
            className={`hidden md:flex  ${classes.logo}`}
            src={logoCobee}
            alt={t("common.logo")}
          />
        </div>
        <div className="col-span-1 items-start w-max">
          <Logout />
        </div>
      </header>
      <div className={classes.infoContainer}>
        <h1 className={classes.title}>
          {t("request-card.phone-number.title")}
        </h1>
        <p className={classes.description}>
          {t("request-card.phone-number.subtitle")}
        </p>
        <form className={classes.formContainer} onSubmit={handleForm}>
          <div className={classes.inputContainer}>
            <select
              className={classes.select}
              id="countryCode"
              onChange={handleOnChange}
            >
              {COUNTRY_CODES.map((country, key) => (
                <option key={key} value={country.code}>
                  {country.flag} {country.code}
                </option>
              ))}
            </select>
            <input
              id="phoneNumber"
              type="tel"
              className={classes.input}
              pattern={regexPhoneNumber}
              onInvalid={handlerOnInvalid}
              onInput={handlerOnInput}
              placeholder={t("request-card.phone-number.number.placeholder")}
              required
            />
          </div>
          <div className={classes.buttonContainer}>
            <Button
              text={t("request-card.add-address.next.button")}
              onClick={handleOnClickFinish}
              type="submit"
            />
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
};

export { AddPhone };
